import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Link, TRACK_EVENT } from '@tkww/the-bash-frontend';
import { styled } from '@mui/material/styles';

import TextField from 'components/TextField';
import AuthenticationSection from 'components/customSections/AuthenticationSection';
import { sendResetPasswordByEmail } from 'api/users';
import { Typography } from '@mui/material';
import { CTA_CLICK } from 'analytics/constants';
import { isEmailValid } from 'utils/email';

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#FFF',
  },
}));

const StyledLoginButtonContainer = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(18),
  [theme.breakpoints.up('md')]: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
}));

const StyledLink = styled(Link)(({ theme }) => ({
  display: 'block',
  marginTop: theme.spacing(3),
}));

const StyledButton = styled(Button)(() => ({
  height: 58,
}));

export const INITIAL_TITLE = 'Reset Password';
export const INITIAL_SUBTITLE =
  "Don't worry! If an account exists we will send you an email with a link to reset your password. We will also remind you of your username.";

export const EMAIL_SENT_TITLE = 'Email Sent';

const ResetPasswordPageView = () => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [wasEmailSent, setWasEmailSent] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const dispatch = useDispatch();

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleSuccessSubmissionTracking = () => {
    dispatch({
      type: TRACK_EVENT,
      name: CTA_CLICK,
      properties: {
        clickTarget: '-------',
        ctaStyle: 'button',
      },
    });
  };

  const handleBackToLoginTracking = () => {
    dispatch({
      type: TRACK_EVENT,
      name: CTA_CLICK,
      properties: {
        clickTarget: 'back to login',
        ctaStyle: 'hyperlink',
      },
    });
  };

  const validate = () => {
    if (!email || !isEmailValid(email)) {
      setErrors({ email: 'Please enter a valid email address.' });
      return false;
    }

    return true;
  };

  const handleResetPassword = async () => {
    try {
      setIsProcessing(true);

      if (validate()) {
        await sendResetPasswordByEmail(email);
        setWasEmailSent(true);
        handleSuccessSubmissionTracking();
      }
    } catch (error) {
      setErrors({
        general: 'An error occurred during login. Please try again or contact us for assistance.',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const getEmailSentSubtitle = () =>
    `Success! If an account exists that is associated with ${email}, we will send a message with your username and instructions on how to reset the password for this account. If you do not receive an email, please contact us for further support.`;

  return (
    <AuthenticationSection
      title={wasEmailSent ? EMAIL_SENT_TITLE : INITIAL_TITLE}
      subtitle={wasEmailSent ? getEmailSentSubtitle() : INITIAL_SUBTITLE}
    >
      {wasEmailSent ? (
        <StyledLink path="/login" onClick={handleBackToLoginTracking}>
          Back To Log In
        </StyledLink>
      ) : (
        <>
          <StyledTextField
            id="email"
            type="email"
            label="Email Address"
            value={email}
            onChange={handleEmailChange}
            error={errors.email}
          />
          <StyledLoginButtonContainer>
            <StyledButton fullWidth onClick={handleResetPassword} loading={isProcessing}>
              Send Reset Link
            </StyledButton>
            {errors.general && (
              <Typography variant="body1" color="error" sx={{ textTransform: 'uppercase', mt: 1 }}>
                {errors.general}
              </Typography>
            )}
          </StyledLoginButtonContainer>
        </>
      )}
    </AuthenticationSection>
  );
};

export default ResetPasswordPageView;
